<i18n src="./resources/locales.json"></i18n>

<template>
  <EcsSideBarWrapper
    class="agent-side-bar"
    :search-placeholder="$t('placeholder')"
    @search-input-changed="handleSearch"
  >
    <v-fade-transition leave-absolute>
      <div v-show="filteredItemsLengthDiff" class="filter-warning" :dark="$vuetify.theme.dark">
        <span v-once>{{ $t('reorderingWarning') }}</span>
      </div>
    </v-fade-transition>

    <v-row no-gutters>
      <v-col>
        <!-- Pending interactions -->
        <EcsSideBarInteractionList
          v-if="displayPendingInteraction"
          :header="$t('pendingInteractions')"
          :interactions="getPendingInteractions"
          is-pending
          show-counter
        />
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col>
        <!-- Dashboards Section -->
        <EcsSideBarTileContent id="ecs-dashboards-section" :header="$t('header.dashboards')">
          <div class="dashboard-list-container">
            <v-progress-circular
              v-show="isLoading"
              :width="2"
              :size="20"
              color="primary"
              class="dashboards-loading"
              indeterminate
            />
            <div class="dashboards-list" :style="{ minHeight: `calc(${filteredDashboards.length} * 56px)` }">
              <v-list-item
                v-for="(dashboard, index) in filteredDashboards"
                :id="dashboard._id"
                :key="dashboard._id || index"
                :disabled="isLoading"
                class="side-bar-item"
                :class="{ active: (selectedDashboard || {})._id === dashboard._id }"
                @click="() => setDashboard(dashboard)"
              >
                <div class="item-content">
                  <v-list-item-content>
                    <v-list-item-title>{{ dashboard.name }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action class="item-actions">
                    <v-menu bottom left class="actions-menu">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon v-bind="attrs" v-on="on" @click="setDashboard(dashboard)">
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>

                      <v-list>
                        <v-list-item
                          v-for="(item, actionIndex) in dashboardActions"
                          :key="actionIndex"
                          @click.prevent="() => item.action(dashboard)"
                        >
                          <v-list-item-icon class="mr-1">
                            <v-icon small>{{ item.icon }}</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>{{ item.title }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-list-item-action>
                </div>
              </v-list-item>
            </div>
          </div>

          <v-list-item class="side-bar-item new-dashboard-item" @click="newDashboard">
            <v-list-item-action>
              <v-icon>mdi-plus</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                <span v-once>{{ $t('addNewDashboard') }}</span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </EcsSideBarTileContent>
      </v-col>
    </v-row>

    <EcsCreateDashboardDialog ref="createDashboardDialog" />
    <EcsConfirmationDialog
      ref="confirmationDialog"
      :title="$t('dialogs.confirmDashboardDeletion.title')"
      :message="confirmDashboardDeletionBodyMessage"
      :confirm-button-text="$t('dialogs.confirmDashboardDeletion.confirm')"
    />
  </EcsSideBarWrapper>
</template>

<script>
import Muuri from 'muuri';
import debounce from 'lodash/debounce';
import { mapActions, mapGetters } from 'vuex';
import EcsSideBarPendingInteractionMixin from '@/mixins/ecs-side-bar-pending-interaction-mixin/EcsSideBarPendingInteractionMixin';
import EcsSideBarInteractionMixins from '@/mixins/ecs-side-bar-interaction-mixins/EcsSideBarInteractionMixins';
import EcsCreateDashboardDialog from '@/components/ecs-create-dashboard-dialog/EcsCreateDashboardDialog';
import EcsConfirmationDialog from '@/components/ecs-confirmation-dialog/EcsConfirmationDialog';

export default {
  name: 'EcsAgentSideBar',

  components: { EcsCreateDashboardDialog, EcsConfirmationDialog },

  mixins: [EcsSideBarInteractionMixins, EcsSideBarPendingInteractionMixin],

  data() {
    return {
      grid: null,
      isLoading: false,
      currentItemsOrder: [],
      selectedListDashboard: () => {}
    };
  },

  computed: {
    ...mapGetters('DASHBOARD', ['defaultDashboard', 'selectedDashboard']),

    ...mapGetters('SETTINGS', ['dashboardSettings']),

    dashboards() {
      if (!this.dashboardSettings) {
        return [];
      }

      return this.dashboardSettings.dashboards;
    },

    filteredDashboards() {
      return this.dashboards.filter(dashboard => {
        return dashboard.name.toLowerCase().includes(this.searchString.toLowerCase());
      });
    },

    filteredItemsLengthDiff() {
      return this.dashboards.length - this.filteredDashboards.length;
    },

    confirmDashboardDeletionBodyMessage() {
      return this.$t('dialogs.confirmDashboardDeletion.body', { name: this.selectedListDashboard.name });
    },

    dashboardActions() {
      const actions = [
        {
          title: this.$t('contextMenu.edit'),
          icon: 'mdi-pencil',
          action: this.editDashboard
        }
      ];

      if (this.dashboards && this.dashboards.length === 1 && this.dashboards[0]._id) {
        actions.push({
          title: this.$t('contextMenu.restore'),
          icon: 'mdi-restore',
          action: this.restoreDashboard
        });
      } else if (this.selectedDashboard && this.selectedDashboard._id) {
        actions.push({
          title: this.$t('contextMenu.delete'),
          icon: 'mdi-delete',
          action: this.deleteDashboard
        });
      }

      return actions;
    }
  },

  watch: {
    filteredItemsLengthDiff() {
      this.initializeMuuri();
    }
  },

  mounted() {
    if (!this.selectedDashboard) {
      if (this.dashboardSettings.dashboards.length) {
        this.setSelectedDashboard(this.dashboardSettings.dashboards[0]);
      } else {
        this.setSelectedDashboard(this.dashboards[0]);
      }
    }

    this.initializeMuuri();
  },

  beforeDestroy() {
    if (this.$root.agentSideBarGrid) {
      this.$root.agentSideBarGrid.off('dragReleaseEnd', this.storeDashboardsOrder);
      this.$root.agentSideBarGrid.destroy(true);
    }
  },

  methods: {
    ...mapActions('DASHBOARD', ['setSelectedDashboard']),

    ...mapActions('SETTINGS', ['updateCurrentUserDashboardSettings', 'createCurrentUserDefaultDashboard']),

    initializeMuuri: debounce(async function() {
      if (this.$router.currentRoute.name !== 'Dashboards') {
        return;
      }

      await this.$nextTick();

      if (this.$root.agentSideBarGrid) {
        this.$root.agentSideBarGrid.destroy();
        delete this.$root.agentSideBarGrid;
      }

      this.$root.agentSideBarGrid = new Muuri('.dashboards-list', {
        layoutEasing: 'cubic-bezier(0.625, 0.225, 0.100, 0.890)',
        layoutDuration: 300,
        dragEnabled: true,
        dragStartPredicate: (item, event) => {
          // Prevents drag & drop when filtering list.
          if (this.filteredItemsLengthDiff) {
            return false;
          }
          return Muuri.ItemDrag.defaultStartPredicate(item, event);
        },
        dragSortHeuristics: {
          sortInterval: 0
        },
        dragRelease: {
          duration: 300,
          easing: 'cubic-bezier(0.625, 0.225, 0.100, 0.890)',
          useDragContainer: true
        },
        dragPlaceholder: {
          enabled: true
        },
        dragAxis: 'y'
      });

      this.$root.agentSideBarGrid.on('dragReleaseEnd', this.storeDashboardsOrder);

      this.currentItemsOrder = this.getMuuriItemsOrder();
    }, 200),

    getMuuriItemsOrder() {
      return this.$root.agentSideBarGrid.getItems().map(item => item.getElement().getAttribute('id'));
    },

    storeDashboardsOrder(draggedDashboardItem) {
      const orderedDashboardsIds = this.getMuuriItemsOrder();
      if (this.currentItemsOrder.join() === orderedDashboardsIds.join()) {
        return;
      }

      const isDefaultDashboardUpdate = (this.dashboardSettings.dashboards || []).length === 0;
      const dashboards = isDefaultDashboardUpdate ? [this.selectedDashboard] : this.dashboardSettings.dashboards;
      let updatedDashboards = [];

      if (isDefaultDashboardUpdate) {
        updatedDashboards = dashboards;
      } else {
        orderedDashboardsIds.forEach(id => {
          updatedDashboards.push(dashboards.find(dashboard => dashboard._id === id));
        });
      }

      this.isLoading = true;
      this.updateCurrentUserDashboardSettings({ dashboards: updatedDashboards })
        .then(({ data }) => {
          const id = draggedDashboardItem.getElement().getAttribute('id');
          this.setSelectedDashboard(data.dashboardSettings.dashboards.find(dashboard => dashboard._id === id));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async editDashboard(dashboard) {
      this.setSelectedDashboard(dashboard);
      await this.$nextTick();
      this.$root.$emit('edit-selected-dashboard');
    },

    restoreDashboard() {
      this.isLoading = true;
      this.createCurrentUserDefaultDashboard().finally(() => {
        this.isLoading = false;
        this.initializeMuuri();
      });
    },

    async deleteDashboard(dashboard) {
      this.selectedListDashboard = dashboard;
      const deleteConfirmed = await this.$refs.confirmationDialog.open();
      if (deleteConfirmed) {
        const dashboards = this.dashboards.filter(item => item._id !== dashboard._id);
        this.isLoading = true;
        this.updateCurrentUserDashboardSettings({ dashboards })
          .then(() => {
            this.$root.$emit('snackbar-message', {
              message: this.$t('successfullyDeletedMessage', { name: dashboard.name }),
              type: 'success'
            });
            if (this.selectedDashboard._id === dashboard._id) {
              this.setSelectedDashboard(null);
            }
          })
          .finally(() => {
            this.isLoading = false;
            this.initializeMuuri();
          });
      }
    },

    newDashboard() {
      this.$refs.createDashboardDialog
        .open()
        .then(this.createDashboard)
        .catch(() => {});
    },

    createDashboard(newDashboard) {
      const dashboards = this.dashboards;
      dashboards.push(newDashboard);
      this.isLoading = true;
      this.updateCurrentUserDashboardSettings({ dashboards })
        .then(() => {
          this.$root.$emit('snackbar-message', {
            message: `${newDashboard.name} dashboard successfully created.`,
            type: 'success'
          });
        })
        .finally(() => {
          this.isLoading = false;
          this.initializeMuuri();
        });
    },

    setDashboard(dashboard) {
      this.setSelectedDashboard(dashboard);
    }
  }
};
</script>

<style src="./scoped.css" scoped></style>
